import { Spine } from "@esotericsoftware/spine-pixi-v8";
import * as PIXI from "pixi.js";
import { useCallback, useEffect, useRef, useState } from "preact/hooks";
import { GameState, GameStateEnum } from "../../../shared/types/global.types";
import { useSkinStore, resolveSpineAsset } from "../../../store/skinStore";
import { CapitalistAnim } from "../PioneerGame/index.enums";
import { useSpineHelpers } from "./useSpineHelpers";

const CAPITALIST_ANIMS = [
  CapitalistAnim.IDLE,
  CapitalistAnim.CLIMB,
  CapitalistAnim.FALL,
] as const;

interface UseSpineCapitalistProps {
  gameState?: GameState;
}

export function useSpineCapitalist({ gameState }: UseSpineCapitalistProps) {
  const { getSkin } = useSkinStore();
  const [currentAnim, setCurrentAnim] = useState<CapitalistAnim | null>(null);
  const capitalistRef = useRef<Spine | null>(null);
  const anims = CAPITALIST_ANIMS;

  const { fitAndPosition } = useSpineHelpers();

  const createCapitalist = useCallback(
    async (parent: PIXI.Container, w: number, h: number) => {
      try {
        const skin = getSkin("pioneer");
        if (!skin.capitalist) {
          throw new Error("Capitalist asset not found in skin");
        }
        const resolved = resolveSpineAsset(skin.capitalist);
        const skel = resolved.skeleton;
        const atl = resolved.atlas;
        await PIXI.Assets.load([skel, atl]);
        const spine = Spine.from({
          skeleton: skel,
          atlas: atl,
          scale: 1,
          autoUpdate: true,
        });
        spine.zIndex = 2; // Выше лестницы
        capitalistRef.current = spine;
        parent.addChild(spine);

        try {
          parent.setChildIndex(spine, Math.max(0, parent.children.length - 2));
        } catch {
          // ignore
        }

        try {
          const idle = CapitalistAnim.IDLE;
          spine.state.setAnimation(0, idle, true);
          setCurrentAnim(idle);
        } catch (e) {
          console.warn(
            "useSpineCapitalist: не удалось собрать/запустить idle анимацию",
            e
          );
        }

        fitAndPosition(spine, w, h);
        spine.state.apply(spine.skeleton);

        return spine;
      } catch (e) {
        console.error("useSpineCapitalist: ошибка создания капиталиста:", e);
        throw e;
      }
    },
    [fitAndPosition, getSkin]
  );
  useEffect(() => {
    const spine = capitalistRef.current;
    if (!spine) return;

    const play = (name: CapitalistAnim, loop: boolean) => {
      try {
        spine.state.setAnimation(0, name, loop);
        setCurrentAnim(name);
      } catch (e) {
        console.warn("useSpineCapitalist: cannot play", name, e);
      }
    };

    if (gameState === GameStateEnum.COUNTDOWN) {
      const idle = CapitalistAnim.IDLE;
      if (currentAnim !== idle) play(idle, true);
      return;
    }

    if (gameState === GameStateEnum.CLIMBING) {
      const climb = CapitalistAnim.CLIMB;
      if (currentAnim !== climb) {
        play(climb, true);
      }
      return;
    }

    if (gameState === GameStateEnum.CRASHED) {
      const fall = CapitalistAnim.FALL;
      if (currentAnim !== fall) play(fall, false);
      return;
    }
  }, [gameState, anims, currentAnim]);

  const changeAnimation = useCallback((name: string) => {
    const spine = capitalistRef.current;
    if (!spine) return;
    if (!CAPITALIST_ANIMS.includes(name as CapitalistAnim)) return;
    const typedName = name as CapitalistAnim;
    try {
      const looping =
        typedName === CapitalistAnim.IDLE || typedName === CapitalistAnim.CLIMB;
      spine.state.setAnimation(0, typedName, looping);
      setCurrentAnim(typedName);
    } catch (e) {
      console.error("useSpineCapitalist: ошибка при смене анимации", e);
    }
  }, []);

  const resizeCapitalist = useCallback(
    (w: number, h: number) => {
      const spine = capitalistRef.current;
      if (!spine) return;
      fitAndPosition(spine, w, h);
    },
    [fitAndPosition]
  );

  const destroyCapitalist = useCallback(() => {
    const spine = capitalistRef.current;
    if (!spine) return;
    spine.destroy({ children: true });
    capitalistRef.current = null;
  }, []);

  return {
    capitalistRef,
    anims,
    currentAnim,
    changeAnimation,
    createCapitalist,
    resizeCapitalist,
    destroyCapitalist,
  } as const;
}
